import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Search, Gift } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import DisclaimerBox from '../components/DisclaimerBox';
import IncomingSection from '../components/IncomingSection';
import { CATEGORIES } from '../mock/mock';
import { getProducts } from '../lib/api';

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const active = params.get('cat') || 'All';
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');

  useEffect(() => {
    setLoading(true);
    getProducts(active)
      .then((data) => setProducts(data))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [active]);

  const setCat = (c) => {
    if (c === 'All') setParams({});
    else setParams({ cat: c });
  };

  const q = query.trim().toLowerCase();
  const visible = q
    ? products.filter((p) => `${p.name} ${p.code || ''} ${p.size || ''}`.toLowerCase().includes(q))
    : products;

  return (
    <div className="bg-alp-black min-h-screen">
      <div className="border-b border-alp-line">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-16 text-center">
          <p className="text-alp-gold text-xs tracking-[0.3em] uppercase mb-3">The Collection</p>
          <h1 className="font-serif text-5xl md:text-6xl text-alp-cream heading-glow">Research Catalog</h1>
          <p className="text-alp-gray mt-4 max-w-xl mx-auto">
            Quality-tested research supplies. Supplied strictly for research purposes only.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 md:px-8 py-12">
        <div className="max-w-md mx-auto mb-10 relative">
          <Search size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-alp-gray" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search products by name or code..."
            data-testid="shop-search-input"
            className="w-full bg-alp-card/50 border border-alp-line rounded-full pl-11 pr-4 py-3 text-sm text-alp-cream placeholder-alp-gray focus:outline-none focus:border-alp-gold transition-colors"
          />
        </div>

        <div className="max-w-3xl mx-auto mb-12">
          <Link to="/gift-cards" data-testid="shop-giftcard-strip"
            className="flex flex-wrap items-center justify-between gap-3 border border-alp-gold/40 rounded-lg bg-alp-gold/5 px-6 py-4 hover:bg-alp-gold/10 transition-colors group">
            <span className="flex items-center gap-3">
              <Gift size={18} className="text-alp-gold shrink-0" />
              <span>
                <span className="block text-alp-cream text-sm font-serif">Digital Gift Cards</span>
                <span className="block text-alp-gray text-xs">Give the gift of luxe — emailed instantly, redeemable on anything, no expiry</span>
              </span>
            </span>
            <span className="text-alp-gold text-xs tracking-[0.2em] uppercase group-hover:translate-x-1 transition-transform">Shop gift cards →</span>
          </Link>
        </div>
        <div className="flex flex-wrap gap-3 justify-center mb-12">
          {['All', ...CATEGORIES].map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              data-testid={`shop-cat-${c.replace(/\s+/g, '-').toLowerCase()}`}
              className={`px-5 py-2.5 text-[11px] tracking-[0.18em] uppercase border rounded-full transition-colors duration-300 ${
                active === c
                  ? 'bg-alp-gold text-alp-black border-alp-gold shadow-[0_0_20px_-4px_rgba(212,180,131,0.5)]'
                  : 'border-alp-line text-alp-cream/70 hover:border-alp-gold/50 hover:text-alp-gold hover:bg-alp-gold/5'
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="shop-loading-skeletons">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="skeleton-card">
                <div className="aspect-[4/5] skeleton-shimmer" />
                <div className="p-5 space-y-3">
                  <div className="h-2.5 w-1/3 rounded skeleton-shimmer" />
                  <div className="h-5 w-2/3 rounded skeleton-shimmer" />
                  <div className="h-3 w-1/4 rounded skeleton-shimmer" />
                </div>
              </div>
            ))}
          </div>
        ) : visible.length === 0 ? (
          <p className="text-center text-alp-gray py-20" data-testid="shop-no-results">{q ? `No products match "${query}".` : 'No products found in this category.'}</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {visible.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        )}

        <IncomingSection />

        <div className="mt-16">
          <DisclaimerBox />
        </div>
      </div>
    </div>
  );
}
